import { useEffect, useState, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useView } from '../contexts/ViewContext';
import { Card, CardHeader } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShoppingCart, TrendingUp, Package, FileText, Loader2, Target, ClipboardList, PackageCheck, CalendarRange } from 'lucide-react';
import { purchasingAPI, coreAPI, platformSettingsAPI, PurchaseOrder, CommodityGroup, CommodityType, Site, CompanyGroup } from '../lib/api';
import type { PurchasingTabsVisibility } from '../lib/api/types/common';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { getPortalLabels } from '../lib/portalLabels';
import { usePortalMode } from '../hooks/usePortalMode';
import { Cache } from '../lib/cache';
import PODetailModal from '../components/purchasing/PODetailModal';
import POHeadersTab from '../components/purchasing/tabs/POHeadersTab';
import ContractPricingTab from '../components/purchasing/tabs/ContractPricingTab';
import POLinesTab from '../components/purchasing/tabs/POLinesTab';
import ASNTab from '../components/purchasing/tabs/ASNTab';
import DeliveryTab from '../components/purchasing/tabs/DeliveryTab';
import ForecastTab from '../components/purchasing/tabs/ForecastTab';
import CallOffTab from '../components/purchasing/tabs/CallOffTab';
import POLineSchedules from './POLineSchedules';
import { usePagination, useModal } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';

// Lazy load RFQ page as tab
const RFQTab = lazy(() => import('./RFQ'));

// Currency symbol mapping for tab icon display
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    JPY: '¥',
    CNY: '¥',
    INR: '₹',
    AUD: 'A$',
    CAD: 'C$',
    CHF: 'Fr',
    MXN: '$',
  };
  return symbols[currency] || currency;
};

export default function Purchasing() {
  const { preferences } = usePreferences();
  const { user } = useAuth();
  const { isFeatureEnabled, isMenuItemVisible, isOpenOrdersControlHidden } = useMenuSettings();
  const { portalMode, isSupplier } = usePortalMode();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalLabels = getPortalLabels(portalMode);

  // Read URL parameters for deep linking
  const urlTab = searchParams.get('tab');
  const urlPo = searchParams.get('po');
  const urlLine = searchParams.get('line');
  const urlSearch = searchParams.get('search');
  const urlLineTab = searchParams.get('lineTab');  // CR-3929: Auto-open specific tab in line detail

  const showSchedules = isFeatureEnabled('po_line_schedules');

  // Purchasing tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<PurchasingTabsVisibility>({
    rfq: true, pricing: true, po_headers: true, po_lines: true,
    forecast: true, asn: true, deliveries: true, call_off: true,
  });
  // CR-5884: Forecast tab can be hidden from supplier viewers via the
  // Admin Setup → Purchasing → Open Orders "Hide Forecasting from Supplier"
  // toggle. Customer-side users always see Forecast.
  const hideForecastForSupplier = isOpenOrdersControlHidden('hide_forecasting_from_supplier', isSupplier);
  const forecastTabVisible = tabVisibility.forecast && !hideForecastForSupplier;
  const [tabsReady, setTabsReady] = useState(false);

  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.purchasing_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.purchasing_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabsReady(true));
  }, []);

  // Call-Off visibility is gated by the per-supplier SupplierCustomerRelationship.call_off_enabled
  // flag, surfaced via isFeatureEnabled('switch_call_off_tab'). The tenant admin can still hide
  // it entirely via menu visibility / tab visibility.
  const showCallOff = tabVisibility.call_off && isMenuItemVisible('callOff') && isFeatureEnabled('switch_call_off_tab');

  // Default tab:
  //  - when schedule_collaboration is enabled, land on Schedules
  //  - otherwise fall back to PO Lines
  //  - explicit ?tab=... in the URL always wins
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  const getValidTab = (tab: string | null): string => {
    if (!tab) return scheduleCollabEnabled ? 'schedules' : 'po_lines';
    // URL deep-links can't escape the tab-visibility rules.
    // ?tab=schedules on a tenant without po_line_schedules falls back
    // to po_lines rather than rendering the Schedules surface without
    // its tab.
    if (tab === 'schedules' && !showSchedules) return 'po_lines';
    return tab;
  };

  const [mainTab, setMainTabState] = useState(getValidTab(urlTab));

  // Sync main tab when URL params change (e.g. notification click while already on page)
  useEffect(() => {
    const newTab = searchParams.get('tab');
    if (newTab && newTab !== mainTab) {
      setMainTabState(getValidTab(newTab));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapper to enforce tab restrictions and persist tab in URL
  const setMainTab = (tab: string) => {
    const validTab = getValidTab(tab);
    setMainTabState(validTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', validTab);
    setSearchParams(next, { replace: true });
  };
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('open');
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [openOrderValue, setOpenOrderValue] = useState<number>(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [poSortField, setPOSortField] = useState('-issue_date');
  const pagination = usePagination({ pageSize: 50 });
  const detailModal = useModal<number>();

  // Text input states (for debouncing) - CR-3954: Initialize from URL search param
  const [textInputs, setTextInputs] = useState({
    poNumber: urlSearch || '',
    supplier: '',
    customer: '',
  });

  // CR-4580: Reset PO Header filters on unmount so they don't persist
  useEffect(() => {
    return () => {
      setFilters({ poNumber: '', supplier: '', customer: '', status: '', dateFrom: '', dateTo: '', site: '', supplierGroup: '', commodityGroup: '', commodityType: '', pdfAttached: '', strategic: '' });
      setTextInputs({ poNumber: '', supplier: '', customer: '' });
    };
  }, []);

  // Actual filter state (used for API calls)
  const [filters, setFilters] = useState({
    poNumber: urlSearch || '',
    supplier: '',
    customer: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    site: '',
    supplierGroup: '',
    commodityGroup: '',
    commodityType: '',
    pdfAttached: '',
    strategic: '',
  });

  // Filter dropdown options
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    setInitialLoadComplete(true);

    // Check cache for filter options (stable reference data, cached 15 min)
    const FILTER_OPTS_CACHE_KEY = 'purchasing_header_filter_options';
    const cached = Cache.get<{
      sites: { value: string; label: string }[];
      commodityGroups: { value: string; label: string }[];
      commodityTypes: { value: string; label: string }[];
      supplierGroups: { value: string; label: string }[];
    }>(FILTER_OPTS_CACHE_KEY);

    if (cached) {
      setSiteOptions(cached.sites);
      setCommodityGroupOptions(cached.commodityGroups);
      setCommodityTypeOptions(cached.commodityTypes);
      setSupplierGroupOptions(cached.supplierGroups);
      return;
    }

    // Fetch filter options for PO Headers
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      purchasingAPI.getCommodityGroups({ enabled: true }).catch(() => ({ data: [] })),
      purchasingAPI.getCommodityTypes({ enabled: true }).catch(() => ({ data: [] })),
      coreAPI.getCompanyGroups({ page_size: 200, company_type: 'supplier' }).catch(() => ({ data: { results: [] } })),
    ]).then(([sitesRes, groupsRes, typesRes, companyGroupsRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      const siteOpts = sites.map((s) => ({ value: String(s.id), label: s.name }));
      setSiteOptions(siteOpts);

      const groups = Array.isArray(groupsRes.data) ? groupsRes.data : (groupsRes.data as unknown as { results: CommodityGroup[] }).results || [];
      const groupOpts = groups.filter((g: CommodityGroup) => g.enabled).map((g: CommodityGroup) => ({ value: String(g.id), label: g.name }));
      setCommodityGroupOptions(groupOpts);

      const types = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data as unknown as { results: CommodityType[] }).results || [];
      const typeOpts = types.filter((t: CommodityType) => t.enabled).map((t: CommodityType) => ({ value: String(t.id), label: t.name }));
      setCommodityTypeOptions(typeOpts);

      const companyGroups = (companyGroupsRes.data as unknown as { results: CompanyGroup[] }).results || [];
      const supplierGroupOpts = companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name }));
      setSupplierGroupOptions(supplierGroupOpts);

      // Cache for 15 minutes (reference data changes rarely)
      Cache.set(FILTER_OPTS_CACHE_KEY, {
        sites: siteOpts,
        commodityGroups: groupOpts,
        commodityTypes: typeOpts,
        supplierGroups: supplierGroupOpts,
      }, 15 * 60 * 1000);
    });
  }, []);

  // CR-3929/CR-3968: Auto-open PO detail or PO line from notification
  useEffect(() => {
    if (urlLine) {
      // Deep link to specific PO line - switch to po_lines tab
      setMainTab('po_lines');
    } else if (urlPo) {
      // Look up PO by number to get the database ID
      purchasingAPI.getOrders({ po_number: urlPo, page_size: 1 }).then((res) => {
        if (res.data.results.length > 0) {
          detailModal.open(res.data.results[0].id);
        }
      }).catch(() => {
        // Silently ignore lookup failures
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPo, urlLine]);

  // Text inputs are applied on explicit Search button click only
  // (no auto-debounce — avoids jittery re-renders while typing)

  useEffect(() => {
    if (viewContextLoading) return;
    // Only fetch PO headers when the PO Headers tab is active.
    // Other tabs (PO Lines, RFQ, etc.) manage their own data fetching.
    if (initialLoadComplete && mainTab === 'po_headers') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, pagination.currentPage, filters, activeTab, initialLoadComplete, mainTab, portalMode, viewKey, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, poSortField]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters based on filters and active tab
      const queryParams: Record<string, string | number | boolean> = {
        ordering: poSortField,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
      };

      // Create a cache key based on current filters and pagination
      const cacheKey = `purchasing_${activeTab}_${pagination.currentPage}_${poSortField}_${JSON.stringify(filters)}`;

      // Check cache first
      const cachedData = Cache.get<{ orders: PurchaseOrder[]; totalCount: number }>(cacheKey);
      if (cachedData) {
        setOrders(cachedData.orders);
        pagination.setFromResponse(cachedData.totalCount);
        setLoading(false);
        return;
      }

      // Individual field filters using Django filter syntax
      if (filters.poNumber) {
        queryParams['po_number__icontains'] = filters.poNumber;
      }

      if (filters.supplier) {
        // Pass the supplier name through as-is so the icontains lookup
        // matches what's actually stored (e.g. "P.P.U DREWAR" with dots).
        // The previous "P.P.U → P P U" normalisation broke filtering for
        // every supplier whose stored name contained dots/dashes/etc.
        queryParams['supplier__name__icontains'] = filters.supplier;
      }

      if (filters.customer) {
        queryParams['customer__name__icontains'] = filters.customer;
      }

      // Apply status filter from dropdown or active tab
      if (filters.status) {
        queryParams['status'] = filters.status;
      } else if (activeTab !== 'all') {
        // Tab-based filtering
        if (activeTab === 'open') {
          queryParams['status__in'] = 'sent,acknowledged,in_progress';
        } else if (activeTab === 'delivered') {
          queryParams['status'] = 'delivered';
        } else if (activeTab === 'closed') {
          queryParams['status'] = 'closed';
        }
        // Note: 'on_hold' tab would need backend support for filtering by line.on_hold
      }

      // Apply date filters
      if (filters.dateFrom) {
        queryParams['issue_date__gte'] = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams['issue_date__lte'] = filters.dateTo;
      }

      // New PO Header filters
      if (filters.site) {
        queryParams['customer'] = filters.site;
      }
      if (filters.supplierGroup) {
        queryParams['supplier__group'] = filters.supplierGroup;
      }
      if (filters.commodityGroup) {
        queryParams['lines__commodity_group'] = filters.commodityGroup;
      }
      if (filters.commodityType) {
        queryParams['lines__commodity_type'] = filters.commodityType;
      }
      if (filters.strategic === 'yes') {
        queryParams['supplier__supplier_profile__is_preferred'] = true;
      }

      // Spread view context params (group/company/site) for multi-customer support
      const viewParams = getViewQueryParams();
      Object.entries(viewParams).forEach(([key, value]) => {
        if (value !== undefined) queryParams[key] = value;
      });

      // Fetch orders and tab counts in parallel.
      // Tab counts must respect the user-applied filters (supplier, supplier_group,
      // commodity, date range, etc.) but NOT the status filter — each tab supplies
      // its own status. Strip status/status__in from the filter set before reuse.
      const { status: _ignoredStatus, status__in: _ignoredStatusIn, ...countFilters } = queryParams;
      void _ignoredStatus; void _ignoredStatusIn;
      const countParams = { ...countFilters, page_size: 1 };
      // Stats endpoint accepts a subset of filters (date_from/date_to/supplier/
      // customer/company/group). Map what we can; backend ignores keys it
      // doesn't know.
      const statsParams: Record<string, string | number | boolean> = {};
      Object.entries(viewParams).forEach(([k, v]) => { if (v !== undefined) statsParams[k] = v; });
      if (filters.dateFrom) statsParams['date_from'] = filters.dateFrom;
      if (filters.dateTo) statsParams['date_to'] = filters.dateTo;
      if (filters.site) statsParams['customer'] = filters.site;
      if (filters.supplier) statsParams['supplier__name__icontains'] = filters.supplier;
      if (filters.supplierGroup) statsParams['supplier__group'] = filters.supplierGroup;
      if (filters.commodityGroup) statsParams['lines__commodity_group'] = filters.commodityGroup;
      if (filters.commodityType) statsParams['lines__commodity_type'] = filters.commodityType;
      const [ordersResponse, allRes, openRes, closedRes, statsRes] = await Promise.all([
        purchasingAPI.getOrders(queryParams),
        purchasingAPI.getOrders(countParams),
        purchasingAPI.getOrders({ ...countParams, status__in: 'sent,acknowledged,in_progress' }),
        purchasingAPI.getOrders({ ...countParams, status: 'closed' }),
        purchasingAPI.getDashboardStats(statsParams),
      ]);

      setOrders(ordersResponse.data.results);
      pagination.setFromResponse(ordersResponse.data.count);
      setTabCounts({
        all: allRes.data.count,
        open: openRes.data.count,
        closed: closedRes.data.count,
      });
      setOpenOrderValue(statsRes.data.open_order_value || 0);

      // Cache the data for 2 minutes (since purchasing data changes more frequently than dashboard)
      Cache.set(cacheKey, {
        orders: ordersResponse.data.results,
        totalCount: ordersResponse.data.count,
      }, 2 * 60 * 1000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load purchasing data'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTextInputs({
      poNumber: '',
      supplier: '',
      customer: '',
    });
    setFilters({
      poNumber: '',
      supplier: '',
      customer: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      site: '',
      supplierGroup: '',
      commodityGroup: '',
      commodityType: '',
      pdfAttached: '',
      strategic: '',
    });
    pagination.reset();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      pagination.setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      poNumber: textInputs.poNumber,
      supplier: textInputs.supplier,
      customer: textInputs.customer,
    };
    setFilters(newFilters);
    pagination.reset();
    // Invalidate cache and fetch immediately with new filters
    const cacheKey = `purchasing_${activeTab}_1_${poSortField}_${JSON.stringify(newFilters)}`;
    Cache.remove(cacheKey);
  };

  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state (will be debounced)
    if (['poNumber', 'supplier', 'customer'].includes(key)) {
      setTextInputs({ ...textInputs, [key]: value });
    } else if (key === 'commodityGroup') {
      // Cascade: reset commodity type when group changes
      setFilters({ ...filters, commodityGroup: value, commodityType: '' });
    } else {
      // Dropdowns and dates: update filters directly (instant)
      setFilters({ ...filters, [key]: value });
    }
    pagination.reset(); // Reset to first page when filtering
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    pagination.reset(); // Reset to first page when changing tabs
  };

  const handlePONumberClick = (poNumber: string) => {
    // CR-5554: only call setSearchParams here — calling setMainTab as well
    // schedules a SECOND setSearchParams from setMainTab's wrapper which
    // reads the STALE searchParams (still without poFilter) and overwrites
    // back to just `?tab=po_lines`, dropping the filter we just added.
    // The useEffect on line ~106 already syncs mainTab from the URL, so
    // setting searchParams alone is sufficient.
    setSearchParams({ tab: 'po_lines', poFilter: poNumber }, { replace: true });
  };

  // Render a loading placeholder for the PO Headers content area (shown inside the tab layout,
  // NOT as a full-page replacement, so the tab bar stays visible and doesn't flicker).
  const renderPOHeadersContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading purchase orders...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return (
      <POHeadersTab
        orders={orders}
        loading={loading}
        totalCount={pagination.totalCount}
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
        onRowClick={(poId) => detailModal.open(poId)}
        onPONumberClick={handlePONumberClick}
        textInputs={textInputs}
        filters={filters}
        siteOptions={siteOptions}
        supplierGroupOptions={supplierGroupOptions}
        commodityGroupOptions={commodityGroupOptions}
        commodityTypeOptions={commodityTypeOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        showFilters={showFilters}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onRefresh={fetchData}
        onSearch={handleSearch}
        onToggleFilters={() => setShowFilters(!showFilters)}
        tabCounts={tabCounts}
        openOrderValue={openOrderValue}
        sortField={poSortField}
        sortOrder={poSortField.startsWith('-') ? 'desc' : 'asc'}
        onSort={(field) => {
          setPOSortField(prev => {
            if (prev === field) return `-${field}`;
            if (prev === `-${field}`) return field;
            return `-${field}`;
          });
          pagination.goToPage(1);
        }}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{portalLabels.ordersPageTitle}</h1>
          <p className="text-muted-foreground mt-2">
            {isSupplier
              ? 'Manage sales orders and track customer deliveries.'
              : 'Manage purchase orders and track procurement activities.'}
          </p>
        </div>
        {/* Filters button moved inside POHeadersTab for consistency with PO Lines */}
      </div>

      {/* KPI Cards - Hidden for now */}
      {/* <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total POs</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_pos || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.total_lines || 0} lines total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spend</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.total_spend || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Total procurement value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Orders</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              £{((stats?.open_order_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Pending/In progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Arrears</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              £{((stats?.arrears_value || 0) / 1000000).toFixed(1)}M
            </div>
            <p className="text-xs text-muted-foreground">
              Overdue orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PPM</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.ppm || 0) > 1500 ? 'text-red-600' : 'text-green-600'}`}>
              {(stats?.ppm || 0).toFixed(0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 1,500 PPM
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTIF</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.otif || 0) >= 85 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats?.otif ? `${stats.otif.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 85%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">OTD</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(stats?.otd || 0) >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
              {stats?.otd ? `${stats.otd.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Target: 90%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">NCRs</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.ncrs || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Non-conformance reports
            </p>
          </CardContent>
        </Card>
      </div> */}

      {/* Main Navigation Tabs */}
      {/* RFQ and Forecast tabs are customer-only (customers create RFQs and share forecasts with suppliers) */}
      <Card className="border-2 shadow-sm">
        <CardHeader className="pb-3">
          {!tabsReady ? (
            /* Skeleton placeholder while tab visibility settings load — prevents tab bar flicker */
            <div className="flex gap-2 animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-1 h-14 bg-slate-100 rounded-md" />
              ))}
            </div>
          ) : (
          <Tabs value={mainTab} onValueChange={setMainTab}>
            <TabsList className={`grid w-full h-auto bg-gradient-to-r from-slate-50 to-slate-100`} style={{ gridTemplateColumns: `repeat(${[
              !isSupplier && tabVisibility.rfq,
              // CR-5552: Pricing / contract pricing / PPV is customer-managed
              // commercial intelligence — must not surface to suppliers in any
              // form. Backend already gates the data; this hides the entry
              // point so suppliers don't even see the tab in their nav.
              !isSupplier && tabVisibility.pricing,
              tabVisibility.po_headers,
              tabVisibility.po_lines,
              showSchedules,
              forecastTabVisible,
              tabVisibility.asn,
              tabVisibility.deliveries,
              showCallOff,
            ].filter(Boolean).length}, minmax(0, 1fr))` }}>
              {/* RFQ tab - Customer only, respects tab visibility */}
              {user?.platform !== 'SUPPLIER' && tabVisibility.rfq && (
                <TabsTrigger value="rfq" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <ClipboardList className={`h-5 w-5 ${mainTab === 'rfq' ? 'text-cyan-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'rfq' ? 'text-cyan-700' : 'text-slate-600'}`}>RFQ</span>
                  </div>
                </TabsTrigger>
              )}
              {!isSupplier && tabVisibility.pricing && (
                <TabsTrigger value="contract_pricing" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <span className={`h-5 w-5 flex items-center justify-center text-lg font-semibold ${mainTab === 'contract_pricing' ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {getCurrencySymbol(preferences.currency)}
                    </span>
                    <span className={`text-xs font-medium ${mainTab === 'contract_pricing' ? 'text-emerald-700' : 'text-slate-600'}`}>Pricing</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.po_headers && (
                <TabsTrigger value="po_headers" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <ShoppingCart className={`h-5 w-5 ${mainTab === 'po_headers' ? 'text-blue-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_headers' ? 'text-blue-700' : 'text-slate-600'}`}>PO Headers</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.po_lines && (
                <TabsTrigger value="po_lines" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <Package className={`h-5 w-5 ${mainTab === 'po_lines' ? 'text-purple-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'po_lines' ? 'text-purple-700' : 'text-slate-600'}`}>PO Lines</span>
                  </div>
                </TabsTrigger>
              )}
              {showSchedules && (
                <TabsTrigger value="schedules" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <CalendarRange className={`h-5 w-5 ${mainTab === 'schedules' ? 'text-cyan-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'schedules' ? 'text-cyan-700' : 'text-slate-600'}`}>Schedules</span>
                  </div>
                </TabsTrigger>
              )}
              {forecastTabVisible && (
                <TabsTrigger value="forecast" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <TrendingUp className={`h-5 w-5 ${mainTab === 'forecast' ? 'text-indigo-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'forecast' ? 'text-indigo-700' : 'text-slate-600'}`}>Forecast</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.asn && (
                <TabsTrigger value="asn" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <FileText className={`h-5 w-5 ${mainTab === 'asn' ? 'text-amber-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'asn' ? 'text-amber-700' : 'text-slate-600'}`}>ASN</span>
                  </div>
                </TabsTrigger>
              )}
              {tabVisibility.deliveries && (
                <TabsTrigger value="delivery" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <Target className={`h-5 w-5 ${mainTab === 'delivery' ? 'text-rose-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'delivery' ? 'text-rose-700' : 'text-slate-600'}`}>Deliveries</span>
                  </div>
                </TabsTrigger>
              )}
              {showCallOff && (
                <TabsTrigger value="call_off" className="py-3 data-[state=active]:bg-white data-[state=active]:shadow-md">
                  <div className="flex flex-col items-center gap-1">
                    <PackageCheck className={`h-5 w-5 ${mainTab === 'call_off' ? 'text-teal-600' : 'text-slate-500'}`} />
                    <span className={`text-xs font-medium ${mainTab === 'call_off' ? 'text-teal-700' : 'text-slate-600'}`}>Call Off</span>
                  </div>
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          )}
        </CardHeader>
      </Card>

      {/* Tab Content */}
      {mainTab === 'rfq' && (
        <Suspense fallback={<div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
          <RFQTab />
        </Suspense>
      )}

      {/* CR-5552: defence-in-depth — even if a supplier deep-links to
          ?tab=contract_pricing the panel never renders for them. */}
      {!isSupplier && mainTab === 'contract_pricing' && <ContractPricingTab />}

      {mainTab === 'po_headers' && renderPOHeadersContent()}

      {mainTab === 'po_lines' && <POLinesTab initialLineId={urlLine ? parseInt(urlLine, 10) : null} initialLineTab={urlLineTab || undefined} />}

      {mainTab === 'schedules' && showSchedules && <POLineSchedules />}

      {mainTab === 'forecast' && forecastTabVisible && <ForecastTab />}

      {mainTab === 'asn' && <ASNTab />}

      {mainTab === 'delivery' && <DeliveryTab />}

      {mainTab === 'call_off' && <CallOffTab />}

      {/* PO Detail Modal */}
      <PODetailModal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        poId={detailModal.data || 0}
      />
    </div>
  );
}
